import { extract, parse } from 'letterparser';
import type { LetterparserNode } from 'letterparser';

import base64ArrayBuffer from '@/components/utils/base64ArrayBuffer';
import type { LetterContent } from '@/types/recipientEmail';

import { hasMimeHeaderBlock } from './candidateMimeHeaders';

const textLeaves = (node: LetterparserNode): string => {
    if (/^attachment(?:;|$)/i.test(node.headers['Content-Disposition'] || '')) return '';
    if (typeof node.body === 'string') {
        return node.contentType.type.startsWith('text/')
            && !['text/html', 'text/x-amp-html'].includes(node.contentType.type) ? node.body : '';
    }
    if (node.body instanceof Uint8Array) return '';
    const nodes = Array.isArray(node.body) ? node.body : [node.body];
    return nodes.map(textLeaves).filter(Boolean).join('\n');
};

export const candidateContentFromNode = (parsed: LetterparserNode): LetterContent => {
    const {
        html, text, subject, attachments,
    } = extract(parsed);

    return {
        html,
        subject,
        plainText: textLeaves(parsed) || text || '',
        attachments: (attachments ?? []).filter(({ contentId, contentType }) => (
            Boolean(contentId) && /^image\/(png|gif|jpe?g|webp|avif|bmp)$/i.test(contentType.type)
        )).map((attachment) => ({
            link: `data:${attachment.contentType.type};base64,${base64ArrayBuffer(attachment.body)}`,
            cid: `cid:${attachment.contentId}`,
        })),
    };
};

const parseCandidateEmail = (raw: string): LetterContent => {
    if (!hasMimeHeaderBlock(raw)) return { plainText: raw, attachments: [] };
    try {
        return candidateContentFromNode(parse(raw));
    } catch {
        return {
            plainText: 'This email has an invalid MIME structure and its message body could not be decoded.',
            attachments: [],
        };
    }
};

export default parseCandidateEmail;
